import React from 'react'
import { useTranslation } from 'react-i18next'
import { FieldPath } from 'react-hook-form'
import EoriSearch from 'features/declaration/form/components/EoriSearch'
import {
  ADDRESS_LINE_MAX_LENGTH,
  ADDRESS_POST_CODE_MAX_LENGTH,
  ADDRESS_STREET_MAX_LENGTH,
  ADDRESS_STREET_NUMBER_MAX_LENGTH,
} from 'shared/ncts/common/constants'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import TraderSearch from 'features/declaration/form/components/TraderSearch'
import { CarrierFormValues as CarrierType } from 'features/declaration/models/schemas/tradersSchema'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import FormCollapsibleColumn from '../../../../../components/p5/FormCollapsibleColumn'
import FormField from '../../../../../components/p5/FormField'
import Input from '../../../../../components/p5/Input'
import FormSelect from '../../../../../components/p5/FormSelect'
import { getSelectCountryFilter } from '../../../../../components/react-select/CountryFunctions'

import DotLeafPaths from '../../../../../helpers/dot-leaf-paths'
import useDeclarationFieldRequirements
  from '../useDeclarationFieldRequirements'

const scope = 'carrier'

type CarrierRelPath = DotLeafPaths<CarrierType>

function Carrier() {
  const { t } = useTranslation()

  const [, countriesOptions] = useCodelist('COUNTRIES')

  const field = (rel: CarrierRelPath) => `${scope}.${rel}` as FieldPath<TransitOperationFormValues>

  const fieldRequirements = useDeclarationFieldRequirements<TransitOperationFormValues>('transitOperation', [
    'carrier',
    'carrier.identificationNumber',
    'carrier.name',
    'carrier.address',
    'carrier.address.country',
    'carrier.address.street',
    'carrier.address.streetNumber',
    'carrier.address.city',
    'carrier.address.postcode',
    'carrier.contactPerson',
    'carrier.contactPerson.name',
    'carrier.contactPerson.telephone',
    'carrier.contactPerson.email',
  ])

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-carrier"
        headingLabel={t('declaration.p5.carrier')}
        fieldRule={fieldRequirements.carrier}
      >
        <FormField
          labelKey="declaration.p5.carrierIdentificationNr"
          fieldRule={fieldRequirements['carrier.identificationNumber']}
        >
          <EoriSearch<TransitOperationFormValues, typeof scope>
            scope={scope}
            traderTarget="CARRIER"
            fieldRule={fieldRequirements['carrier.identificationNumber']}
          />
        </FormField>
        <FormField
          scope={`${scope}.name`}
          labelKey="declaration.p5.carrierName"
          fieldRule={fieldRequirements['carrier.name']}
        >
          <TraderSearch
            scope={scope}
            maxLength={ADDRESS_LINE_MAX_LENGTH}
            traderTarget="CARRIER"
            fieldRule={fieldRequirements['carrier.name']}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.country"
          fieldRule={fieldRequirements['carrier.address.country']}
        >
          <FormSelect
            field={field('address.country')}
            labelKey="declaration.p5.country"
            options={countriesOptions}
            customFilterOption={getSelectCountryFilter}
            type="sync"
            fieldRule={fieldRequirements['carrier.address.country']}
          />
        </FormField>
        <div className="row party-address-row">
          <FormField
            className="col-6"
            labelKey="declaration.p5.street"
            fieldRule={fieldRequirements['carrier.address.street']}
          >
            <Input
              field={field('address.street')}
              autoComplete={`${scope}.address.street`}
              labelKey="declaration.p5.street"
              type="text"
              maxLength={ADDRESS_STREET_MAX_LENGTH}
              placeholder={t('declaration.p5.street')}
              fieldRule={fieldRequirements['carrier.address.street']}
            />
          </FormField>
          <FormField
            className="col-6"
            labelKey="declaration.p5.streetNumber"
            fieldRule={fieldRequirements['carrier.address.streetNumber']}
          >
            <Input
              field={field('address.streetNumber')}
              autoComplete={`${scope}.address.streetNumber`}
              labelKey="declaration.p5.streetNumber"
              type="text"
              maxLength={ADDRESS_STREET_NUMBER_MAX_LENGTH}
              placeholder={t('declaration.p5.streetNumber')}
              fieldRule={fieldRequirements['carrier.address.streetNumber']}
            />
          </FormField>
        </div>
        <div className="row party-address-row">
          <FormField
            className="col-8"
            labelKey="declaration.p5.city"
            fieldRule={fieldRequirements['carrier.address.city']}
          >
            <Input
              field={field('address.city')}
              autoComplete={`${scope}.address.city`}
              labelKey="declaration.p5.city"
              maxLength={35}
              type="text"
              placeholder={t('declaration.p5.city')}
              fieldRule={fieldRequirements['carrier.address.city']}
            />
          </FormField>
          <FormField
            className="col-4"
            labelKey="declaration.p5.postcode"
            fieldRule={fieldRequirements['carrier.address.postcode']}
          >
            <Input
              field={field('address.postcode')}
              autoComplete={`${scope}.address.postcode`}
              labelKey="declaration.p5.postcode"
              maxLength={ADDRESS_POST_CODE_MAX_LENGTH}
              type="text"
              placeholder={t('declaration.p5.postcode')}
              fieldRule={fieldRequirements['carrier.address.postcode']}
            />
          </FormField>
        </div>
        <FormField
          labelKey="declaration.p5.contactPersonName"
          fieldRule={fieldRequirements['carrier.contactPerson.name']}
        >
          <Input
            field={field('contactPerson.name')}
            labelKey="declaration.p5.contactPersonName"
            type="text"
            placeholder={t('declaration.p5.contactPersonName')}
            fieldRule={fieldRequirements['carrier.contactPerson.name']}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.contactPersonPhone"
          fieldRule={fieldRequirements['carrier.contactPerson.telephone']}
        >
          <Input
            field={field('contactPerson.telephone')}
            labelKey="declaration.p5.contactPersonPhone"
            type="text"
            placeholder={t('declaration.p5.contactPersonPhone')}
            fieldRule={fieldRequirements['carrier.contactPerson.telephone']}
          />
        </FormField>
        <FormField
          labelKey="declaration.p5.contactPersonEmail"
          fieldRule={fieldRequirements['carrier.contactPerson.email']}
        >
          <Input
            field={field('contactPerson.email')}
            labelKey="declaration.p5.contactPersonEmail"
            type="text"
            placeholder={t('declaration.p5.contactPersonEmail')}
            fieldRule={fieldRequirements['carrier.contactPerson.email']}
          />
        </FormField>
      </FormCollapsibleColumn>
    </section>
  )
}

export default Carrier
